import React, { useState, useEffect } from "react";
import ReusableEditor, { getEditorConfig } from "../../components/commonComponent/CommanEditor";
import { FaUpload, FaPlus, FaMinus, FaTrash } from "react-icons/fa";
import axiosInstance from "../../utils/axiosInstance";
import { toastSuccess, toastError } from "../../utils/alertHelper";

const IMAGE_BASE_URL = process.env.REACT_APP_Image_URL || "https://api.solarestimate.in/image/";
const MAX_SOCIAL_LINKS = 8;

export default function QuotationSetting() {
  const [activeTab, setActiveTab] = useState("header");
  const [loading, setLoading] = useState(false);
  const [settingId, setSettingId] = useState(null);

  const businessId = localStorage.getItem("businessId") || "";

  const editorConfig = getEditorConfig({ minHeight: "560px" });

  const [formData, setFormData] = useState({
    name: localStorage.getItem("businessName") || "",
    showheader: false,
    showfooter: false,
    showcommercialtc: false,
    commercialtc: "",
    showresidentialtc: false,
    residentialtc: "",
    showscopofwork: false,
    scopofwork: "",
    showprivacypolicy: false,
    privacypolicy: "",
    showwarranty: false,
    warranty: "",
    showbankdetail: false,
    bankdetail: "",
    showupi: false,
    upiImage: null,
    showsignature: false,
    signature: null,
    showsociallinks: false,
    coverImage: null,
    showAboutPage: false,
    aboutCoverImage: null,
    aboutTitle: "",
    aboutDescription: "",
    showProductPage: false,
    aboutUs: {
      title: "",
      description: "",
      image: null,
    },
    products: {
      title: "",
      images: [
      ],
    },
    themeColor: "#5A66A8",
    textColor: "#000000",
  });

  const [socialLinks, setSocialLinks] = useState([{ id: Date.now(), _id: null, name: "", link: "", logo: null }]);

  const getDisplayUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("data:")) return path;
    if (path.startsWith("http")) return path;
    return `${IMAGE_BASE_URL}${path}`;
  };

  const allowedTypes = [
    "image/jpeg", "image/jpg", "image/png", "application/pdf"
  ];
  const maxSize = 2 * 1024 * 1024; 
  async function handleValidatedImageUpload(file) {
    if (!file) return null;
    if (!allowedTypes.includes(file.type)) {
      toastError("Only JPG, JPEG, PNG, and PDF files are allowed.");
      return null;
    }
    if (file.size > maxSize) {
      toastError("File size exceeds 2MB.");
      return null;
    }
    
    return await fileToBase64(file);
  }

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  // ----------------------------------------------------------------------

  const fetchQuotationSettings = async (idParam) => {
    try {
      setLoading(true);
      let response;
      if (idParam) {
        response = await axiosInstance.get(`/admin/api/quatation-setting/${idParam}`);
      } else {
        response = await axiosInstance.get('/admin/api/quatation-setting', {
          params: businessId ? { business: businessId } : {}
        });
      }

      if (response.data.Status === 1) {
        let settings = response.data.quatationSettings?.[0] || response.data.quatationSetting;
        if (settings) {
          setSettingId(settings._id);
          setFormData(prev => ({
            ...prev,
            name: settings.name && settings.name.trim() ? settings.name : prev.name,
            showheader: settings.showheader ?? false,
            showfooter: settings.showfooter ?? false,
            showcommercialtc: settings.showcommercialtc ?? false,
            commercialtc: settings.commercialtc || "",
            showresidentialtc: settings.showresidentialtc ?? false,
            residentialtc: settings.residentialtc || "",
            showscopofwork: settings.showscopofwork ?? false,
            scopofwork: settings.scopofwork || "",
            showprivacypolicy: settings.showprivacypolicy ?? false,
            privacypolicy: settings.privacypolicy || "",
            showwarranty: settings.showwarranty ?? false,
            warranty: settings.warranty || "",
            showbankdetail: settings.showbankdetail ?? false,
            bankdetail: settings.bankdetail || "",
            showupi: settings.showupi ?? false,
            upiImage: getDisplayUrl(settings.upiImage) || prev.upiImage,
            showsignature: settings.showsignature ?? false,
            signature: getDisplayUrl(settings.signature) || prev.signature,
            showsociallinks: settings.showsociallinks ?? false,
            coverImage: getDisplayUrl(settings.homeImage || settings.coverImage) || prev.coverImage,
            showAboutPage: settings.showAboutPage ?? false,
            aboutCoverImage: getDisplayUrl(settings.aboutUs?.image) || prev.aboutCoverImage,
            aboutTitle: settings.aboutUs?.title || "",
            aboutDescription: settings.aboutUs?.description || "yhjyjjj",
            aboutUs: {
              title: settings.aboutUs?.title || "",
              description: settings.aboutUs?.description || "",
              image: getDisplayUrl(settings.aboutUsImage || settings.aboutUs?.image) || prev.aboutUs?.image,
            },
            products: {
              title: settings.products?.title || "",
              images: Array.isArray(settings.products?.images || settings.products?.image)
                ? (settings.products?.images || settings.products?.image).map(getDisplayUrl)
                : (prev.products?.images || []),
            },
            showProductPage: settings.showProductPage ?? false,
            themeColor: settings.themeColor || "#5A66A8",
            textColor: settings.textColor || "#000000",
          }));

          const savedLinks = (settings.socialmedialinks || []).filter(l => l.link?.trim());
          setSocialLinks(
            savedLinks.length > 0
              ? savedLinks.map((link, i) => ({
                  id: Date.now() + i,
                  _id: link._id || link.id || null,
                  name: link.name || "",
                  link: link.link || "",
                  logo: getDisplayUrl(link.logo),
                }))
                  : [{ id: Date.now(), _id: null, name: "", link: "", logo: null }]
          );
        } else {
          setSettingId(null);
              setSocialLinks([{ id: Date.now(), _id: null, name: "", link: "", logo: null }]);
        }
      }
    } catch (error) {
      toastError("Failed to load settings");
      setSocialLinks([{ id: Date.now(), _id: null, name: "", link: "", logo: null }]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (fieldPath, file) => {
    if (!file) return;
    const base64 = await handleValidatedImageUpload(file); 
    if (!base64) return;
    if (fieldPath.includes(".")) {
      const [parent, child] = fieldPath.split(".");
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: base64 }
      }));
    } else {
      setFormData(prev => ({ ...prev, [fieldPath]: base64 }));
    }
  };

  const handleProductUpload = async (e) => {
    const files = Array.from(e.target.files);
    const current = formData.products.images.length;
    const allowed = 4 - current;
    if (allowed <= 0) {
      toastError("Maximum 4 images allowed");
      return;
    }
    const toProcess = files.slice(0, allowed);
    
    const uploadPromises = toProcess.map(handleValidatedImageUpload);
    const base64Array = (await Promise.all(uploadPromises)).filter(Boolean);
    setFormData(prev => ({
      ...prev,
      products: { ...prev.products, images: [...prev.products.images, ...base64Array] },
    }));
    e.target.value = "";
  };

  const removeProductImage = (index) => {
    setFormData(prev => ({
      ...prev,
      products: { ...prev.products, images: prev.products.images.filter((_, i) => i !== index) },
    }));
  };

  const addSocialLink = () => {
    if (socialLinks.length >= MAX_SOCIAL_LINKS) {
      toastError(`Maximum ${MAX_SOCIAL_LINKS} social links allowed`);
      return;
    }
    setSocialLinks(prev => [...prev, { id: Date.now(), _id: null, name: "", link: "", logo: null }]);
  };

  const removeSocialLink = (id) => {
    if (socialLinks.length === 1) return;
    setSocialLinks(prev => prev.filter(link => link.id !== id));
  };

  const updateSocialLink = (id, field, value) => {
    setSocialLinks(prev => prev.map(link => (link.id === id ? { ...link, [field]: value } : link)));
  };

  const handleSocialLogoUpload = async (id, file) => {
    if (!file) return;
    const base64 = await handleValidatedImageUpload(file); 
    if (!base64) return;
    updateSocialLink(id, "logo", base64);
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateProducts = (key, value) => {
    setFormData(prev => ({
      ...prev,
      products: { ...prev.products, [key]: value },
    }));
  };

  const saveQuotationSettings = async () => {
    try {
      setLoading(true);
      const payload = {
        name: formData.name,
        showheader: formData.showheader,
        showfooter: formData.showfooter,
        showcommercialtc: formData.showcommercialtc,
        commercialtc: formData.commercialtc,
        showresidentialtc: formData.showresidentialtc,
        residentialtc: formData.residentialtc,
        showscopofwork: formData.showscopofwork,
        scopofwork: formData.scopofwork,
        showprivacypolicy: formData.showprivacypolicy,
        privacypolicy: formData.privacypolicy,
        showwarranty: formData.showwarranty,
        warranty: formData.warranty,
        showbankdetail: formData.showbankdetail,
        bankdetail: formData.bankdetail,
        showupi: formData.showupi,
        showsignature: formData.showsignature,
        showsociallinks: formData.showsociallinks,
        showAboutPage: formData.showAboutPage,
        showProductPage: formData.showProductPage,
        // aboutTitle: formData.aboutTitle,
        // aboutDescription: formData.aboutDescription,
        themeColor: formData.themeColor,
        textColor: formData.textColor,
        business: businessId,
      };

      const fd = new FormData();

     
      Object.keys(payload).forEach(key => {
        const val = payload[key];
        if (val === undefined || val === null) return;
        if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
          fd.append(key, String(val));
        } else {
          fd.append(key, JSON.stringify(val));
        }
      });

     
      const dataURLtoFile = (dataurl, filename) => {
        try {
          const arr = dataurl.split(',');
          const mimeMatch = arr[0].match(/:(.*?);/);
          const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
          }
          return new File([u8arr], filename, { type: mime });
        } catch (err) {
          return null;
        }
      };

      
      const appendImageIfData = (fieldName, value, baseName) => {
        if (!value) return;
        // If value is a local default asset path, do not append/send to server
        if (typeof value === 'string' && value.startsWith('/assets/images/')) return;
        if (typeof value === 'string' && value.startsWith('data:')) {
          const f = dataURLtoFile(value, `${baseName}_${Date.now()}.png`);
          if (f) fd.append(fieldName, f);
        }
        
      };

      appendImageIfData('upiImage', formData.upiImage, 'upi');
      appendImageIfData('signature', formData.signature, 'signature');
      // Backend expects `homeImage` for the cover image; keep UI state as `coverImage`
      appendImageIfData('homeImage', formData.coverImage, 'cover');
      appendImageIfData('aboutUsImage', formData.aboutCoverImage, 'aboutCover');

      
      // fd.append('aboutUs[title]', formData.aboutUs?.title || '');
      // fd.append('aboutUs[description]', formData.aboutUs?.description || '');
      
      fd.append('aboutUsTitle', formData.aboutUs?.title || '');
      fd.append('aboutUsDescription', formData.aboutUs?.description || '');
      appendImageIfData('aboutUs[image]', formData.aboutUs?.image, 'aboutUs');
      // Also append `aboutUsImage` field for backend compatibility
      if (formData.aboutUs?.image) {
        const v = formData.aboutUs.image;
        // Skip appending local default preview asset paths
        if (typeof v === 'string' && v.startsWith('/assets/images/')) {
          // do nothing
        } else if (typeof v === 'string' && v.startsWith('data:')) {
          const f = dataURLtoFile(v, `aboutUs_${Date.now()}.png`);
          if (f) fd.append('aboutUsImage', f);
        } else if (v instanceof File) {
          fd.append('aboutUsImage', v);
        } else if (typeof v === 'string') {
          fd.append('aboutUsImage', v);
        }
      }

     
      // Build single structured `products` payload with image array.
      const prodImgs = formData.products?.images || [];
      // Append each image as its own product entry: products[0][image], products[1][image], ...
      prodImgs.forEach((img, idx) => {
        if (!img) return;
        // skip local default assets (UI-only)
        if (typeof img === 'string' && img.startsWith('/assets/images/')) return;
        const key = `products[${idx}][image]`;
        if (typeof img === 'string') {
          if (img.startsWith('data:')) {
            const f = dataURLtoFile(img, `product_${idx}_${Date.now()}.png`);
            if (f) fd.append(key, f);
          } else {
            // existing path or URL: send as string (strip IMAGE_BASE_URL if present)
            const pathVal = img.startsWith('http') ? (img.startsWith(IMAGE_BASE_URL) ? img.replace(IMAGE_BASE_URL, '') : img) : img;
            fd.append(key, pathVal);
          }
        } else if (img instanceof File) {
          fd.append(key, img);
        }
      });

      // Append single structured products object
      // fd.append('products', JSON.stringify({ title: formData.products?.title || '', image: productsImageArray }));
      fd.append('productTitles', formData.products?.title);
      // Build social links payload. For logos:
      // - If logo is a data URL -> convert to File and append under multipart `socialmedialinks[<i>][logo]`.
      // - If logo is a File already -> append as File.
      // - If logo is an existing URL/path string -> keep path in JSON (don't append as file) to avoid server errors.
      const socialPayload = socialLinks.map((l) => {
        let logoPath = null;
        if (l.logo) {
          if (typeof l.logo === 'string') {
            // If it's a data URL we'll append a file later; if it's a remote URL, strip IMAGE_BASE_URL and store path
            if (l.logo.startsWith('data:')) {
              logoPath = null; // file will be appended
            } else if (l.logo.startsWith('http')) {
              // keep full URL in JSON (server may accept full URL) or strip base if server expects path
              logoPath = l.logo.startsWith(IMAGE_BASE_URL) ? l.logo.replace(IMAGE_BASE_URL, '') : l.logo;
            } else {
              // likely already a server path
              logoPath = l.logo;
            }
          } else if (l.logo instanceof File) {
            logoPath = null; // file will be appended
          }
        }

        return { _id: l._id || null, id: l._id || null, name: l.name || '', link: l.link || '', logo: logoPath || '' };
      });

      fd.append('socialmedialinks', JSON.stringify(socialPayload));

      for (let i = 0; i < socialLinks.length; i++) {
        const l = socialLinks[i];
        const logoKey = `socialmedialinks[${i}][logo]`;
        if (!l.logo) continue;
        if (typeof l.logo === 'string' && l.logo.startsWith('data:')) {
          const f = dataURLtoFile(l.logo, `social_${Date.now()}_${i}.png`);
          if (f) fd.append(logoKey, f);
        } else if (l.logo instanceof File) {
          fd.append(logoKey, l.logo);
        }
        // Do NOT append plain URL strings as files (server expects either files or paths in JSON)
      }

      try {
        const preview = [];
        
        for (const pair of fd.entries()) {
          const [k, v] = pair;
          if (v instanceof File) preview.push(`${k}: File(name=${v.name}, type=${v.type}, size=${v.size})`);
          else preview.push(`${k}: ${typeof v}(${String(v).slice(0, 120)})`);
        }
        
        console.log('QuotationSetting: FormData preview →', preview);
      } catch (err) {
        
        console.warn('QuotationSetting: failed to preview FormData', err);
      }

      let response = null;
      if (settingId) {
        response = await axiosInstance.put(`/admin/api/quatation-setting/${settingId}`, fd, {
          headers: { 'Accept': 'application/json' }
        });
      } else {
        response = await axiosInstance.post('/admin/api/quatation-setting', fd, {
          headers: { 'Accept': 'application/json' }
        });
      }

      if (response?.data?.Status === 1) {
        toastSuccess("Settings saved successfully");
        await fetchQuotationSettings();
      } else {
        toastError("Failed to save settings");
      }
    } catch (error) {
      console.error('saveQuotationSettings error:', error?.message || error, error?.response || error?.request || {});
      const serverMessage = error?.response?.data?.message || error?.response?.data?.error || null;
      if (serverMessage) {
        toastError(String(serverMessage));
      } else {
        toastError("Error saving settings");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    await saveQuotationSettings();
  };

 
  const handleCancel = () => {
    if (settingId) fetchQuotationSettings(settingId);
    else fetchQuotationSettings();
  };

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qid = params.get('quotationId') || params.get('id');
      if (qid) {
        fetchQuotationSettings(qid);
        return;
      }
    } catch (e) {
      
    }
    if (businessId) fetchQuotationSettings();
   
  }, [businessId]);

  return (
    <div className="container-fluid mt-4" style={{ position: "relative" }}>
      {loading && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999 }}>
          <div className="text-white text-center">
            <div className="spinner-border text-light mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div>Loading...</div>
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
        <h2 className="h2 mb-0 text-dark">Quotation Setting</h2>
      </div>

      <ul className="nav nav-tabs mb-4 gap-1">
        <li className="nav-item">
          <button className={`nav-link custom-tab-btn ${activeTab === "header" ? "active-tab" : ""}`} onClick={() => setActiveTab("header")}>
            Cover Page
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link custom-tab-btn ${activeTab === "about" ? "active-tab" : ""}`} onClick={() => setActiveTab("about")}>
            About US
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link custom-tab-btn ${activeTab === "product" ? "active-tab" : ""}`} onClick={() => setActiveTab("product")}>
            Product Image
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link custom-tab-btn ${activeTab === "footer" ? "active-tab" : ""}`} onClick={() => setActiveTab("footer")}>
            Term & Declaration
          </button>
        </li>
      </ul>

      {/* Header Tab */}
      {activeTab === "header" && (
        <div className="d-flex justify-content-center mt-3">
          <div className="w-100" style={{ maxWidth: "1600px" }}>
            <div className="rounded-4 p-5 mt-4 shadow-sm" style={{ background: "#F6F6F6", border: "1.5px solid #BBBFD2", borderRadius: "16px" }}>
              <div className="row g-3">
                <div className="col-md-3 col-sm-4">
                  <label className="fw-semibold mb-2">Theme Color</label>
                  <input type="color" className="form-control form-control-color w-50" value={formData.themeColor} onChange={e => updateFormData("themeColor", e.target.value)} />
                </div>
                <div className="col-md-3 col-sm-4">
                  <label className="fw-semibold mb-2">Text Color</label>
                  <input type="color" className="form-control form-control-color w-50" value={formData.textColor} onChange={e => updateFormData("textColor", e.target.value)} />
                </div>
              </div>

              <div className="mt-4">
                <label className="fw-semibold mb-2">Cover Image</label>
                <div className="border rounded-3 p-4 text-center bg-white" style={{ maxWidth: "300px" }}>
                  <input type="file" accept="image/*" className="d-none" id="coverImageUpload" onChange={e => handleImageUpload("coverImage", e.target.files[0])} />
                  <label htmlFor="coverImageUpload" className="cursor-pointer d-flex flex-column align-items-center" style={{ cursor: "pointer" }}>
                    <FaUpload size={22} className="mb-2 text-primary" />
                    <span className="fw-semibold">Upload Image</span>
                    <small className="text-muted">Size (585 × 415)</small>
                  </label>
                  {formData.coverImage && <img src={formData.coverImage} alt="Cover Preview" className="img-fluid mt-3 rounded" />}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* About Tab */}
      {activeTab === "about" && (
        <div className="d-flex justify-content-center mt-3">
          <div className="w-100" style={{ maxWidth: "1600px" }}>
            <div className="rounded-4 p-5 mt-4 shadow-sm" style={{ background: "#F6F6F6", border: "1.5px solid #BBBFD2", borderRadius: "16px" }}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={formData.showAboutPage} onChange={e => updateFormData("showAboutPage", e.target.checked)} />
                  <label className="form-check-label fw-semibold ms-2">Show About Us Page</label>
                </div>
              </div>

              <div className="mb-4">
                <label className="fw-semibold mb-2">Cover Image</label>
                <div className="border rounded-3 p-4 text-center bg-white" style={{ maxWidth: "320px" }}>
                  <input type="file" accept="image/*" className="d-none" id="aboutCoverImage" onChange={e => handleImageUpload("aboutCoverImage", e.target.files[0])} />
                  <label htmlFor="aboutCoverImage" className="cursor-pointer d-flex flex-column align-items-center" style={{ cursor: "pointer" }}>
                    <FaUpload size={22} className="mb-2 text-primary" />
                    <span className="fw-semibold">Upload Image</span>
                    <small className="text-muted">Size (585 × 415)</small>
                  </label>
                  {formData.aboutCoverImage && <img src={formData.aboutCoverImage} alt="About Cover" className="img-fluid mt-3 rounded" />}
                </div>
              </div>

              <div className="mb-4">
                <label className="fw-semibold mb-2">Title</label>
                <input type="text" className="form-control w-25" placeholder="Enter title" value={formData.aboutTitle} onChange={e => updateFormData("aboutTitle", e.target.value)} />
              </div>

              <div className="mb-5">
                <label className="fw-semibold mb-2">Description</label>
                <ReusableEditor value={formData.aboutDescription} onChange={value => updateFormData("aboutDescription", value)} placeholder="Enter description..." />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Tab */}
      {activeTab === "product" && (
        <div className="d-flex justify-content-center mt-3">
          <div className="w-100" style={{ maxWidth: "1600px" }}>
            <div className="rounded-4 p-5 mt-4 shadow-sm" style={{ background: "#F6F6F6", border: "1.5px solid #BBBFD2", borderRadius: "16px" }}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={formData.showProductPage} onChange={e => updateFormData("showProductPage", e.target.checked)} />
                  <label className="form-check-label fw-semibold ms-2">Show Product Page</label>
                </div>
              </div>

              <div className="mb-4">
                <label className="fw-semibold mb-2">Title</label>
                <input type="text" className="form-control w-25" placeholder="Enter title" value={formData.products.title} onChange={e => updateProducts("title", e.target.value)} />
              </div>

              <div className="mb-4">
                <label className="fw-semibold mb-3">Product Images (Max 4)</label>
                <div className="row g-3 mb-3">
                  {formData.products.images.map((image, index) => (
                    <div key={index} className="col-md-3 col-sm-6">
                      <div className="position-relative border rounded-3 p-2 bg-white">
                        <img src={image} alt={`Product ${index + 1}`} className="img-fluid rounded" style={{ width: "100%", height: "200px", objectFit: "cover" }} />
                        <button type="button" className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2" onClick={() => removeProductImage(index)}>
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </div>
                  ))}

                  {formData.products.images.length < 4 && (
                    <div className="col-md-3 col-sm-6">
                      <label htmlFor="ProductImagesUpload" className="border border-2 border-dashed rounded-3 p-4 text-center bg-white d-flex flex-column align-items-center justify-content-center" style={{ height: "220px", cursor: "pointer" }}>
                        <FaUpload size={32} className="mb-3 text-primary" />
                        <span className="fw-semibold">Upload Image</span>
                        <small className="text-muted mt-2">{4 - formData.products.images.length} slots remaining</small>
                      </label>
                      <input type="file" accept="image/*" multiple className="d-none" id="ProductImagesUpload" onChange={handleProductUpload} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "footer" && (
        <div className="d-flex justify-content-center mt-3">
          <div className="w-100" style={{ maxWidth: "1600px" }}>
            <div
              className="rounded-4 p-5 mt-4 shadow-sm border-1"
              style={{
                background: "#F6F6F6",
                border: "1.5px solid #BBBFD2",
                borderRadius: "16px",
              }}
            >
              <div className="d-flex justify-content-start mb-3">
                <div className="form-check form-switch d-flex align-items-center gap-3">
                  <label className="form-check-label fw-semibold fs-6 mb-0" htmlFor="showFooter">
                    Show Footer
                  </label>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showFooter"
                    checked={formData.showfooter}
                    onChange={(e) => updateFormData('showfooter', e.target.checked)}
                    style={{ transform: "scale(1.3)" }}
                  />
                </div>
              </div>
              <hr className="border-2 mb-4" />
              <div className="rounded-4 p-4 shadow-sm"
                style={{
                  background: "#F6F6F6",
                  border: "1.5px solid #BBBFD2",
                  borderRadius: "16px",
                }}
              >
                <div className="row g-4 mb-4">
                  <div className="col-md-6">
                    <div
                      className="form-check form-switch d-flex align-items-center gap-3 mb-2"
                      style={{ marginLeft: "30px" }}
                    >
                      <label
                        className="form-check-label fw-semibold fs-6 mb-0"
                        htmlFor="showCommTerms"
                      >
                        Show Commercial Terms & Conditions
                      </label>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="showCommTerms"
                        checked={formData.showcommercialtc}
                        onChange={(e) => updateFormData('showcommercialtc', e.target.checked)}
                        style={{ transform: "scale(1.3)" }}
                      />
                    </div>
                    <div className="quotation-setting" style={{ height: "150px" }}>
                      <ReusableEditor
                        value={formData.commercialtc}
                        onChange={(data) => updateFormData('commercialtc', data)}
                        config={editorConfig}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div
                      className="form-check form-switch d-flex align-items-center gap-3 mb-2"
                      style={{ marginLeft: "30px" }}
                    >
                      <label
                        className="form-check-label fw-semibold fs-6 mb-0"
                        htmlFor="showResidential"
                      >
                        Show Residential Terms & Conditions
                      </label>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="showResidential"
                        checked={formData.showresidentialtc}
                        onChange={(e) => updateFormData('showresidentialtc', e.target.checked)}
                        style={{ transform: "scale(1.3)" }}
                      />
                    </div>
                    <div className="quotation-setting" style={{ height: "150px" }}>
                      <ReusableEditor
                        value={formData.residentialtc}
                        onChange={(data) => updateFormData('residentialtc', data)}
                        config={editorConfig}
                      />
                    </div>
                  </div>
                </div>
                <hr className="mt-2 mb-0 border-secondary" />
                <div className="col-md-7">
                  <div
                    className="form-check form-switch d-flex align-items-center gap-3 mb-2"
                    style={{ marginLeft: "20px" }}
                  >
                    <label
                      className="form-check-label fw-semibold fs-6 mb-0"
                      htmlFor="showScope"
                    >
                      Scope of Work
                    </label>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showScope"
                      checked={formData.showscopofwork}
                      onChange={(e) => updateFormData('showscopofwork', e.target.checked)}
                      style={{ transform: "scale(1.3)" }}
                    />
                  </div>
                  <div className="quotation-setting" style={{ height: "150px" }}>
                    <ReusableEditor
                      value={formData.scopofwork}
                      onChange={(data) => updateFormData('scopofwork', data)}
                      config={editorConfig}
                    />
                  </div>
                </div>
                <hr className="mt-2 mb-0 border-secondary" />
                <div className="col-md-7">
                  <div
                    className="form-check form-switch d-flex align-items-center gap-3 mb-2"
                    style={{ marginLeft: "20px" }}
                  >
                    <label
                      className="form-check-label fw-semibold fs-6 mb-0"
                      htmlFor="showPrivacy"
                    >
                      Privacy Policy
                    </label>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showPrivacy"
                      checked={formData.showprivacypolicy}
                      onChange={(e) => updateFormData('showprivacypolicy', e.target.checked)}
                      style={{ transform: "scale(1.3)" }}
                    />
                  </div>
                  <div className="quotation-setting" style={{ height: "150px" }}>
                    <ReusableEditor
                      value={formData.privacypolicy}
                      onChange={(data) => updateFormData('privacypolicy', data)}
                      config={editorConfig}
                    />
                  </div>
                </div>
                <hr className="mt-2 mb-0 border-secondary opacity-20" />
                <div className="col-md-7">
                  <div
                    className="form-check form-switch d-flex align-items-center gap-3 mb-2"
                    style={{ marginLeft: "20px" }}
                  >
                    <label
                      className="form-check-label fw-semibold fs-6 mb-0"
                      htmlFor="showWarranty"
                    >
                      Warranty
                    </label>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showWarranty"
                      checked={formData.showwarranty}
                      onChange={(e) => updateFormData('showwarranty', e.target.checked)}
                      style={{ transform: "scale(1.3)" }}
                    />
                  </div>
                  <div className="quotation-setting" style={{ height: "150px" }}>
                    <ReusableEditor
                      value={formData.warranty}
                      onChange={(data) => updateFormData('warranty', data)}
                      config={editorConfig}
                    />
                  </div>
                </div>
                <hr className="mt-2 mb-0 border-secondary opacity-20" />
                <div className="col-md-7">
                  <div
                    className="form-check form-switch d-flex align-items-center gap-3 mb-2"
                    style={{ marginLeft: "20px" }}
                  >
                    <label
                      className="form-check-label fw-semibold fs-6 mb-0"
                      htmlFor="showBank"
                    >
                      Bank Details
                    </label>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showBank"
                      checked={formData.showbankdetail}
                      onChange={(e) => updateFormData('showbankdetail', e.target.checked)}
                      style={{ transform: "scale(1.3)" }}
                    />
                  </div>
                  <div className="quotation-setting" style={{ height: "150px" }}>
                    <ReusableEditor
                      value={formData.bankdetail}
                      onChange={(data) => updateFormData('bankdetail', data)}
                      config={editorConfig}
                    />
                  </div>
                </div>
              </div>

              <div className="row align-items-start mb-4" style={{ marginTop: "10px" }}>
                <div className="col-md-6 d-flex flex-column align-items-start">
                  <div className="form-check form-switch d-flex align-items-center justify-content-between mb-2" style={{ width: 180, marginLeft: "30px", marginTop: "10px" }}>
                    <label className="form-check-label fw-semibold fs-6 mb-0">Show UPI</label>
                    <input className="form-check-input" type="checkbox" checked={formData.showupi} onChange={e => updateFormData("showupi", e.target.checked)} style={{ transform: "scale(1.3)" }} />
                  </div>
                  <label className="form-label fw-semibold mt-2 mb-2" style={{ marginLeft: "30px" }}>UPI QR</label>
                  <div className="border border-2 border-dashed rounded-3 p-3 bg-light text-center" style={{ width: 180, marginLeft: "30px", backgroundColor: "#F6F6F6" }}>
                    <label htmlFor="upload-qr" className="d-block" style={{ cursor: "pointer" }}>
                      <FaUpload className="mb-2 text-primary" size={20} />
                      <div style={{ fontSize: 14, color: "#868686" }}>Upload QR Code</div>
                    </label>
                    <input id="upload-qr" type="file" style={{ display: "none" }} accept="image/*" onChange={e => handleImageUpload("upiImage", e.target.files[0])} />
                    <div className="rounded mt-3 bg-white border d-flex align-items-center justify-content-center" style={{ width: 80, height: 80, objectFit: "cover", margin: "0 auto", overflow: "hidden" }}>
                      {formData.upiImage ? <img src={formData.upiImage} alt="UPI QR" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span className="text-muted" style={{ fontSize: 12 }}>No Image</span>}
                    </div>
                  </div>
                </div>

                <div className="col-md-6 d-flex flex-column align-items-start">
                  <div className="form-check form-switch d-flex align-items-center justify-content-between mb-2" style={{ width: 230, marginLeft: "30px", marginTop: "10px" }}>
                    <label className="form-check-label fw-semibold fs-6 mb-0">Show Signature in Footer</label>
                    <input className="form-check-input" type="checkbox" checked={formData.showsignature} onChange={e => updateFormData("showsignature", e.target.checked)} style={{ transform: "scale(1.3)" }} />
                  </div>
                  <label className="form-label fw-semibold mt-2 mb-2" style={{ marginLeft: "30px" }}>Signature</label>
                  <div className="border border-2 border-dashed rounded-3 p-3 bg-light text-center" style={{ width: 180, marginLeft: "30px", backgroundColor: "#F6F6F6" }}>
                    <label htmlFor="upload-signature" className="d-block" style={{ cursor: "pointer" }}>
                      <FaUpload className="mb-2 text-primary" size={20} />
                      <div style={{ fontSize: 14, color: "#868686" }}>Upload Signature</div>
                    </label>
                    <input id="upload-signature" type="file" style={{ display: "none" }} accept="image/*" onChange={e => handleImageUpload("signature", e.target.files[0])} />
                    <div className="rounded mt-3 bg-white border d-flex align-items-center justify-content-center" style={{ width: 80, height: 80, objectFit: "cover", margin: "0 auto", overflow: "hidden" }}>
                      {formData.signature ? <img src={formData.signature} alt="Signature" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span className="text-muted" style={{ fontSize: 12 }}>No Image</span>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-check form-switch d-flex align-items-center justify-content-between mb-4" style={{ width: 250, marginLeft: "10px" }}>
                <label className="form-check-label fw-semibold fs-6 mb-0">Show Social Links</label>
                <input className="form-check-input" type="checkbox" checked={formData.showsociallinks} onChange={e => updateFormData("showsociallinks", e.target.checked)} style={{ transform: "scale(1.3)" }} />
              </div>
              <div className="row g-4">
                <div className="col-12">
                  {socialLinks.map((socialLink, index) => (
                      <div className="row g-3" key={socialLink.id}>
                        <div className="col-md-6">
                          <label className="small fw-semibold">Link & Logo</label>
                          <div className="input-group rounded-3 overflow-hidden">
                            <span className="input-group-text bg-white border-end d-flex align-items-center justify-content-center" style={{ width: 48, cursor: "pointer" }} onClick={() => document.getElementById(`logo-upload-${socialLink.id}`).click()}>
                              {socialLink.logo ? <img src={socialLink.logo} alt="logo" width="22" height="22" style={{ objectFit: "cover", borderRadius: "2px" }} /> : <FaUpload className="text-muted" size={16} />}
                            </span>
                            <input type="text" className="form-control border-0" placeholder="https://www.example.com/profile" value={socialLink.link} onChange={e => updateSocialLink(socialLink.id, "link", e.target.value)} />
                            <button type="button" className="btn btn-light border-start" onClick={() => removeSocialLink(socialLink.id)} disabled={socialLinks.length === 1}>
                              <FaMinus className="text-danger" size={14} />
                            </button>
                          </div>
                          <input id={`logo-upload-${socialLink.id}`} type="file" style={{ display: "none" }} accept="image/*" onChange={e => handleSocialLogoUpload(socialLink.id, e.target.files[0])} />
                        </div>
                      </div>
                  ))}
                  <button type="button" className="btn btn-outline-primary d-flex align-items-center gap-2" onClick={addSocialLink}>
                    <FaPlus size={14} /> Add Social Link
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="position-sticky bottom-0 bg-white mt-4" style={{ zIndex: 100 }}>
        <div className="d-flex justify-content-end gap-3 container-fluid">
          <button type="button" className="btn btn-secondary px-4" onClick={handleCancel} disabled={loading}>Cancel</button>
          <button type="button" className="btn btn-primary px-4" onClick={handleSave} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}